/**
 * Time-only HH:mm picker — web native `<input type="time">`, native DateTimePicker.
 * Control chrome matches InventoryIsoDateField (shared temporal tokens).
 *
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { Fragment, useState } from 'react';
import { Modal } from '../modals/Modal';
import {
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import DateTimePicker from '@react-native-community/datetimepicker';
import { iosDatePickerDisplay } from './iosDatePickerDisplay';
import { useModalBackdropPadding } from './hooks-ui/useModalBackdropPadding';
import { WebTimeInput } from './WebTimeInput';
import {
  nativeTemporalTriggerStyle,
  NATIVE_TEMPORAL_MIN_HEIGHT,
  NATIVE_TEMPORAL_COMFORTABLE_HEIGHT,
  NATIVE_TEMPORAL_RADIUS,
} from './webNativeTemporalInput';
import { colors } from '../theme/colors';
import { spacing } from '../theme/spacing';
import { typography } from '../theme/typography';
import { ticketColors } from '../theme/ticketColors';

type Props = {
  value: string;
  onChange: (hm: string) => void;
  accessibilityLabel?: string;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
  comfortable?: boolean;
};

function normalizeHm(hm: string): string | null {
  const m = hm.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min) || h > 23 || min > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

function hmToDate(hm: string): Date {
  const n = normalizeHm(hm) ?? '09:00';
  const [h, m] = n.split(':').map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
}

function dateToHm(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function displayHm(hm: string): string {
  return normalizeHm(hm) ?? (hm.trim() || '––:––');
}

export function HmTimeField({
  value,
  onChange,
  accessibilityLabel,
  disabled = false,
  style,
  comfortable = false,
}: Props) {
  const iosMode = iosDatePickerDisplay();
  const iosModalBackdropPadding = useModalBackdropPadding();
  const [androidOpen, setAndroidOpen] = useState(false);
  const [iosSheetOpen, setIosSheetOpen] = useState(false);
  const [iosDraft, setIosDraft] = useState(() => hmToDate(value));

  const commit = (d: Date) => onChange(dateToHm(d));

  if (Platform.OS === 'web') {
    return (
      <WebTimeInput
        value={normalizeHm(value) ?? ''}
        onChange={onChange}
        ariaLabel={accessibilityLabel}
        disabled={disabled}
        size={comfortable ? 'comfortable' : 'default'}
        style={style}
      />
    );
  }

  if (Platform.OS === 'android') {
    return (
      <Fragment>
        <TouchableOpacity
          style={[styles.trigger, comfortable && styles.triggerComfortable, disabled && styles.disabled, style]}
          onPress={() => {
            if (!disabled) setAndroidOpen(true);
          }}
          disabled={disabled}
          accessibilityRole="button"
          accessibilityLabel={accessibilityLabel}
        >
          <Text style={[styles.value, comfortable && styles.valueComfortable]}>{displayHm(value)}</Text>
        </TouchableOpacity>
        {androidOpen ? (
          <DateTimePicker
            value={hmToDate(value)}
            mode="time"
            display="default"
            is24Hour
            locale="es-AR"
            onValueChange={(_e, date) => {
              setAndroidOpen(false);
              if (date) commit(date);
            }}
            onDismiss={() => setAndroidOpen(false)}
          />
        ) : null}
      </Fragment>
    );
  }

  if (iosMode === 'inline') {
    return (
      <View style={[styles.iosCompactWrap, comfortable && styles.triggerComfortable, disabled && styles.disabled, style]}>
        <DateTimePicker
          value={hmToDate(value)}
          mode="time"
          display="compact"
          locale="es-AR"
          disabled={disabled}
          onValueChange={(_e, date) => {
            if (date) commit(date);
          }}
          style={styles.iosCompact}
        />
      </View>
    );
  }

  return (
    <View style={[styles.wrap, style]}>
      <TouchableOpacity
        style={[styles.trigger, comfortable && styles.triggerComfortable, disabled && styles.disabled]}
        onPress={() => {
          if (disabled) return;
          setIosDraft(hmToDate(value));
          setIosSheetOpen(true);
        }}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
      >
        <Text style={[styles.value, comfortable && styles.valueComfortable]}>{displayHm(value)}</Text>
      </TouchableOpacity>
      <Modal
        visible={iosSheetOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setIosSheetOpen(false)}
      >
        <View style={[styles.iosBackdrop, iosModalBackdropPadding]}>
          <View style={styles.iosCard}>
            {accessibilityLabel ? <Text style={styles.iosTitle}>{accessibilityLabel}</Text> : null}
            <DateTimePicker
              value={iosDraft}
              mode="time"
              display="spinner"
              locale="es-AR"
              onValueChange={(_e, date) => {
                if (date) setIosDraft(date);
              }}
              style={styles.iosSpinner}
            />
            <View style={styles.iosActions}>
              <TouchableOpacity style={styles.outlineBtn} onPress={() => setIosSheetOpen(false)}>
                <Text style={styles.outlineBtnText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.primaryBtn}
                onPress={() => {
                  commit(iosDraft);
                  setIosSheetOpen(false);
                }}
              >
                <Text style={styles.primaryBtnText}>Listo</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { flex: 1, minWidth: 0 },
  trigger: {
    ...nativeTemporalTriggerStyle,
    flex: 1,
    justifyContent: 'center',
  },
  triggerComfortable: {
    minHeight: NATIVE_TEMPORAL_COMFORTABLE_HEIGHT,
  },
  value: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.textPrimary,
    fontVariant: ['tabular-nums'],
  },
  valueComfortable: {
    fontSize: typography.body.fontSize,
  },
  disabled: { opacity: 0.5 },
  iosCompactWrap: {
    flex: 1,
    minWidth: 0,
    borderWidth: 1,
    borderColor: colors.controlBorder,
    borderRadius: NATIVE_TEMPORAL_RADIUS,
    backgroundColor: colors.inputBackground,
    minHeight: NATIVE_TEMPORAL_MIN_HEIGHT,
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  iosCompact: { alignSelf: 'center' },
  iosBackdrop: {
    flex: 1,
    backgroundColor: colors.overlay,
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
  },
  iosCard: {
    backgroundColor: colors.surface,
    borderRadius: 14,
    padding: spacing.md,
    gap: spacing.sm,
    borderWidth: 1,
    borderColor: colors.controlBorder,
    width: '100%',
    maxWidth: 360,
    alignSelf: 'center',
  },
  iosTitle: {
    ...typography.body,
    fontWeight: '700',
    color: colors.textPrimary,
  },
  iosSpinner: { alignSelf: 'center', width: '100%', height: 216 },
  iosActions: { flexDirection: 'row', gap: spacing.sm, justifyContent: 'flex-end' },
  outlineBtn: {
    paddingHorizontal: spacing.md,
    paddingVertical: 10,
    borderRadius: NATIVE_TEMPORAL_RADIUS,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.controlBorder,
    minHeight: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  outlineBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textPrimary,
  },
  primaryBtn: {
    backgroundColor: colors.accent,
    borderRadius: NATIVE_TEMPORAL_RADIUS,
    paddingVertical: 10,
    paddingHorizontal: spacing.md,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryBtnText: {
    fontWeight: '700',
    color: ticketColors.goldInk,
  },
});
